import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  Select,
  Button,
  VStack,
  HStack,
  useToast,
  Spinner,
  Flex,
} from '@chakra-ui/react';
import PromotionsApi from '../../../../api/promotions';
import { useCompaniesOptions } from '../../../../hooks/useCompaniesOptions';

const PromotionFormModal = ({ isOpen, onClose, promotion, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    imageUrl: '',
    webUrl: '',
    showOnce: false,
    triggerType: '',
    variant: '',
    companyId: '',
    startDate: '',
    endDate: '',
  });

  const toast = useToast();
  const { companiesOptions } = useCompaniesOptions({ showAll: false });
  const isEditMode = !!promotion;

  useEffect(() => {
    if (promotion) {
      setFormData({
        title: promotion.title || '',
        body: promotion.body || '',
        imageUrl: promotion.imageUrl || '',
        webUrl: promotion.webUrl || '',
        showOnce: promotion.showOnce || false,
        triggerType: promotion.triggerType || '',
        variant: promotion.variant || '',
        companyId: promotion.companyId ? String(promotion.companyId) : '',
        startDate: promotion.startDate || '',
        endDate: promotion.endDate || '',
      });
    } else {
      setFormData({
        title: '',
        body: '',
        imageUrl: '',
        webUrl: '',
        showOnce: false,
        triggerType: '',
        variant: '',
        companyId: '',
        startDate: '',
        endDate: '',
      });
    }
  }, [promotion, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast({
        title: 'נא להזין כותרת',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        title: formData.title,
        body: formData.body || null,
        imageUrl: formData.imageUrl || null,
        webUrl: formData.webUrl || null,
        showOnce: formData.showOnce,
        triggerType: formData.triggerType || null,
        variant: formData.variant || null,
        companyId: formData.companyId ? parseInt(formData.companyId) : null,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
      };

      if (isEditMode) {
        await PromotionsApi.updatePromotion(promotion.id, payload);
        toast({
          title: 'פרומושן עודכן בהצלחה',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } else {
        await PromotionsApi.createPromotion(payload);
        toast({
          title: 'פרומושן נוצר בהצלחה',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving promotion:', error);
      toast({
        title: isEditMode ? 'שגיאה בעדכון פרומושן' : 'שגיאה ביצירת פרומושן',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEditMode ? 'עריכת פרומושן' : 'יצירת פרומושן חדש'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>כותרת</FormLabel>
              <Input
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="הזן כותרת"
              />
            </FormControl>

            <FormControl>
              <FormLabel>תוכן</FormLabel>
              <Textarea
                value={formData.body}
                onChange={(e) => handleChange('body', e.target.value)}
                placeholder="הזן תוכן הפרומושן"
                rows={3}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Image URL</FormLabel>
              <Input
                value={formData.imageUrl}
                onChange={(e) => handleChange('imageUrl', e.target.value)}
                placeholder="https://example.com/image.png"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Web URL</FormLabel>
              <Input
                value={formData.webUrl}
                onChange={(e) => handleChange('webUrl', e.target.value)}
                placeholder="https://example.com"
              />
            </FormControl>

            <HStack spacing={4}>
              <FormControl>
                <FormLabel>Trigger Type</FormLabel>
                <Select
                  value={formData.triggerType}
                  onChange={(e) => handleChange('triggerType', e.target.value)}
                  placeholder="בחר סוג"
                >
                  <option value="home">Home</option>
                  <option value="post-transfer">Post Transfer</option>
                  <option value="settings">Settings</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Variant</FormLabel>
                <Select
                  value={formData.variant}
                  onChange={(e) => handleChange('variant', e.target.value)}
                  placeholder="בחר variant"
                >
                  <option value="inactive">Inactive (0 transfers)</option>
                  <option value="active">Active (1+ transfers)</option>
                  <option value="all">All users</option>
                </Select>
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel>חברה (ריק = כל החברות)</FormLabel>
              <Select
                value={formData.companyId}
                onChange={(e) => handleChange('companyId', e.target.value)}
                placeholder="כל החברות (גלובלי)"
              >
                {companiesOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <HStack spacing={4}>
              <FormControl>
                <FormLabel>תאריך התחלה</FormLabel>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel>תאריך סיום</FormLabel>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                />
              </FormControl>
            </HStack>

            <FormControl>
              <Flex align="center">
                <FormLabel mb={0} mr={3}>הצג פעם אחת</FormLabel>
                <Switch
                  isChecked={formData.showOnce}
                  onChange={(e) => handleChange('showOnce', e.target.checked)}
                  colorScheme="blue"
                />
              </Flex>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            ביטול
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="שומר..."
          >
            {isEditMode ? 'עדכן' : 'צור'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PromotionFormModal;

